import ModalBackButton from "../components/ModalBackButton";
import SecondaryButton from "../components/SecondaryButton";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { minUSDAmountByPaymentMethod } from "../lib/minUSDAmountByPaymentMethod";
import CoinbaseLogo from "../logos/CoinbaseLogo";
import MoonPayLogo from "../logos/MoonPayLogo";
import { InteracLogo } from "../logos/InteracLogo";
import VisaMastercardLogo from "../logos/VisaMastercardLogo";
import OnramperLogo from "../logos/OnramperLogo";

export type OnrampPaymentMethod =
  | "coinbase"
  | "fiat"
  | "interac"
  | "debit_card_alias"
  | "onramper";

type Props = {
  availablePaymentMethods?: string[] | null;
  txAmountUSD?: number;
  selectedPaymentMethod?: OnrampPaymentMethod;
  onSelect: (paymentMethod: OnrampPaymentMethod) => void;
  onBack: () => void;
};

export function OnrampProviderSelector({
  availablePaymentMethods,
  txAmountUSD,
  selectedPaymentMethod,
  onSelect,
  onBack,
}: Props) {
  const depositConfig = useGlideDepositConfig();

  // Helper to check if a payment method is available from the backend
  const isMethodAvailable = (method: string) => {
    // If availablePaymentMethods is null (not loaded or error), allow all methods
    if (!availablePaymentMethods) return true;
    return availablePaymentMethods.includes(method);
  };

  // Calculate available onramp methods
  const buyTokensPaymentMethods = depositConfig.popupsBlocked
    ? []
    : depositConfig.paymentMethods
        .filter(
          (method) =>
            method === "coinbase" ||
            method === "fiat" ||
            method === "interac" ||
            method === "debit_card_alias" ||
            (method === "onramper" && depositConfig.mode === "deposit"),
        )
        .filter((method) => {
          if (!txAmountUSD) return true;

          // For debit_card_alias, use the minimum from its actual alias
          const methodToCheck =
            method === "debit_card_alias"
              ? depositConfig.debitCardAlias || "fiat"
              : method;
          return txAmountUSD >= minUSDAmountByPaymentMethod[methodToCheck];
        })
        .filter(
          (method) => !depositConfig.excludeFundingSources?.includes(method),
        )
        .filter((method) => isMethodAvailable(method));

  return (
    <>
      <ModalBackButton onClick={onBack} />

      <TransactionCheckoutHeader
        title={
          depositConfig.copyOverrides?.["fundingSource.onramp.title"] ||
          (depositConfig.mode === "pay"
            ? "Buy crypto & pay"
            : depositConfig.mode === "deposit" &&
                depositConfig.hideSettlementCopy
              ? "Deposit with fiat"
              : `Buy ${depositConfig.currency?.symbol || "tokens"}`)
        }
      />

      <div className="space-y-2">
        {buyTokensPaymentMethods.map((method) => {
          switch (method) {
            case "debit_card_alias":
              return (
                <SecondaryButton
                  key={method}
                  icon={<VisaMastercardLogo className="h-6 w-6" />}
                  title="Debit card"
                  loading={selectedPaymentMethod === "debit_card_alias"}
                  onClick={() => onSelect("debit_card_alias")}
                  disabled={depositConfig.chain?.isTestnet}
                />
              );
            case "coinbase":
              return (
                <SecondaryButton
                  key={method}
                  icon={<CoinbaseLogo className="h-6 w-6" />}
                  title="Coinbase"
                  loading={selectedPaymentMethod === "coinbase"}
                  onClick={() => onSelect("coinbase")}
                  disabled={depositConfig.chain?.isTestnet}
                />
              );
            case "fiat":
              return (
                <SecondaryButton
                  key={method}
                  icon={<MoonPayLogo className="h-6 w-6" />}
                  title={
                    depositConfig.copyOverrides?.[
                      "fundingSource.moonpay.title"
                    ] || "MoonPay"
                  }
                  loading={selectedPaymentMethod === "fiat"}
                  onClick={() => onSelect("fiat")}
                  disabled={depositConfig.chain?.isTestnet}
                />
              );
            case "interac":
              return (
                <SecondaryButton
                  key={method}
                  icon={<InteracLogo className="h-6 w-6" />}
                  title="Interac"
                  loading={selectedPaymentMethod === "interac"}
                  onClick={() => onSelect("interac")}
                  disabled={depositConfig.chain?.isTestnet}
                />
              );
            case "onramper":
              return (
                <SecondaryButton
                  key={method}
                  icon={<OnramperLogo className="h-6 w-6" />}
                  title="Onramper"
                  loading={selectedPaymentMethod === "onramper"}
                  onClick={() => onSelect("onramper")}
                  disabled={depositConfig.chain?.isTestnet}
                />
              );
            default:
              return null;
          }
        })}
      </div>
    </>
  );
}
